import DevJSONView from '@/components/DevJSONView';
import ClientSchemaProvider, { useClientSchemaContext } from '@/context/clientSchemaContext';
import { isProspectFieldVisible, VIEW_TYPES } from '@/features/config/helper';
import {
	Box,
	Button,
	CardContent,
	CircularProgress,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import AddressFields from '../CreateClientForm/Fields/AddressFields';
import ClientStatusFields from '../CreateClientForm/Fields/ClientStatusFields';
import ClientNameFields from '../CreateClientForm/Fields/ClientNameFields';
import ClientCategoryFields from '../CreateClientForm/Fields/ClientCategoryFields';
import CreationInfoFields from '../CreateClientForm/Fields/CreationInfoFields';
import ClientInfoFields from '../CreateClientForm/Fields/ClientInfoFields';
import BankInfoFields from '../CreateClientForm/Fields/BankInfoFields';
import LegalInfoFields from '../CreateClientForm/Fields/LegalInfoFields';
import CategoryFields from '../CreateClientForm/Fields/CategoryFields';
import { FormikErrorScroll } from '@/hooks/useFormikHelper';
import { showToast } from '@/features/toast/toastSlice';
import { useGetFormConfigQuery } from '@/features/config/configApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import ContactFields from '../CreateClientForm/Fields/ContactFields';
import { useUpdateContactMutation } from '@/features/contacts/contactsApi';
import { useGetClientRowQuery, useUpdateClientxMutation } from '@/features/clients/clientsApi';
import { useParams } from 'react-router-dom';
import { parseInsightContactToFirebase } from '@/features/contacts/helper';
import { difference, replaceFolderName } from '@/utils/constants';
import { AddContactDialog } from '@/components/dialogs/AddContactDialog';
import { useRenameSharepointItemMutation } from '@/features/msGraph/msGraphApi';
import { FolioDrawerHeader } from '@/components/Drawers/FolioDrawer/FolioDrawerHeader';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';
import { FolioDrawerFormGuard } from '@/components/Drawers/FolioDrawer/FolioDrawerFormGuard';
import ClientClassificationFields from '../CreateClientForm/Fields/ClientClassificationFields';
import { getClientFormError } from '@/utils/constants';
import { buildSupportTicketPrefill } from '@/components/dialogs/IssueReportingDialog/supportTicketPrefill';

const VIEW = VIEW_TYPES.update;

const UpdateClientFormWrapper = ({ dialogView = false, onSubmit }) => {
	const params = useParams();
	const dispatch = useDispatch();
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const [updateContact, updateContactState] = useUpdateContactMutation();
	const [updateClient, updateClientState] = useUpdateClientxMutation();
	const [renameSharepointItem] = useRenameSharepointItemMutation();

	const dialogData = useSelector((state) => state.dialog);

	const clientId = params?.clientId ?? dialogData?.data?.clientDialog?.clientId;
	const { data: clientRow } = useGetClientRowQuery(clientId, {
		skip: !clientId,
	});

	const {
		isProspect,
		view,
		initialValues,
		schema: validationSchema,
		isLoading,
	} = useClientSchemaContext();

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const fieldsQuery = useGetFormConfigQuery(
		{ configType: 'client' },
		{
			skip: !userDetails,
			selectFromResult: ({ data, ...rest }) => ({
				...rest,
				data:
					data?.filter((f) =>
						isProspect ? isProspectFieldVisible(f, view) : f.visible?.[view],
					) ?? [],
			}),
		},
	);

	const fields = useMemo(() => {
		return (fieldsQuery.data ?? []).slice().map((f) => ({
			...f,
			required: f.mandatory?.[isProspect ? 'prospect' : 'client'],
		}));
	}, [fieldsQuery.data, isProspect]);

	const handleUpdateClient = async ({
		values,
		contacts,
		setFieldTouched,
		setFieldError,
		setSubmitting,
	}) => {
		setSubmitting(true);
		const mainContact = contacts?.find((c) => c.isMainContact);
		const initialMainContact = initialValues.contacts?.find((c) => c.isMainContact);
		const data = Object.keys(difference(initialValues, values)).reduce(
			(acc, val) => {
				const key = val.split('-')[0];
				const fieldData = fields.find((f) => f.name === key);
				if (val.endsWith('-type')) return acc;
				acc[fieldData?.source ?? 'firebase'][val] = values[val];
				if (val === 'brokerGroupId-docId') {
					acc['firebase']['brokerGroupId'] = values[val];
				}
				// SharePoint rejects tabs in folder names, so keep the client name free of them
				if (val === 'Name') {
					acc['insight']['Name'] = values[val].replace(/\s+/g, ' ').trim();
				}
				return acc;
			},
			{
				insight: {
					...(mainContact &&
						Object.keys(difference(initialMainContact, mainContact)).length > 0 && {
							mainContact,
						}),
				},
				firebase: {},
			},
		);

		console.log('[onSubmit] Data going to update client', data);
		try {
			if (Object.keys(data.insight).length === 0 && Object.keys(data.firebase).length === 0) {
				return { success: true };
			}
			// Update the client
			const res = await updateClient({
				clientId,
				...data,
			}).unwrap();
			if (data.insight.Name) {
				const newFolderName = replaceFolderName(data.insight.Name);
				await renameSharepointItem({
					driveId: clientRow?.clientFolderDriveId,
					itemId: clientRow?.driveId,
					name: newFolderName,
				})
					.unwrap()
					.catch((e) => {
						console.log('Error updating client folder name', e?.message);
						throw e;
					});
			}
			console.log('[onSubmit] Update client res', res);
			return { success: true };
		} catch (e) {
			console.error('[onSubmit] Error updating client', e);
			const { message, formFieldName } = getClientFormError(e);

			dispatch(
				showToast({
					message: `Error updating client: ${message}`,
					error: true,
					autohide: false,
					action: {
						label: 'Report Issue',
						dialogName: 'issueReporting',
						data: buildSupportTicketPrefill({
							subject: 'Error updating client',
							errorMessage: message,
							areaImpacted: 'Clients',
						}),
					},
				}),
			);
			if (formFieldName) {
				try {
					console.log('[onSubmit] Field', formFieldName);
					setFieldTouched(formFieldName, true);
					setTimeout(() => {
						setFieldError(formFieldName, message);
					}, 100);
				} catch (err) {
					console.log('Could not set field error', err);
				}
			}
			return { error: true };
		} finally {
			setSubmitting(false);
		}
	};

	const handleUpdateContacts = async (contacts) => {
		try {
			// Update each contact
			for (let i = 0; i < contacts.length; i++) {
				const contact = contacts[i];
				const diffFields = difference(initialValues.contacts[i], contact);

				if (Object.keys(diffFields).length > 0) {
					const {
						Contact,
						Name,
						BirthDate,
						Email,
						Telephone,
						Telephone2,
						JobTitle,
						Mobile,
						Fax,
						Salutation,
						isMainContact,
					} = diffFields;
					const contactRes = await updateContact({
						insightId: contact.Id,
						clientId: clientRow?.clientId,
						Name: Contact ?? Name,
						BirthDate,
						Email,
						Telephone,
						Telephone2,
						JobTitle,
						Mobile,
						Fax,
						Salutation,
						IsPrimary: isMainContact,
					}).unwrap();
					console.log('Update contact res', contactRes);
				}
			}
			return { success: true };
		} catch (e) {
			console.error('[onSubmit] Error updating contact', e);
			const message = e.data?.message ?? e?.message;

			dispatch(
				showToast({
					message: `Updated client, but had error updating contacts: ${message}`,
					error: true,
				}),
			);
			return { error: true };
		}
	};

	return (
		<Formik
			{...{
				initialValues,
				validationSchema,
				validateOnBlur: true,
				enableReinitialize: true,
				onSubmit: async (
					values,
					{ setFieldError, resetForm, setFieldTouched, setSubmitting },
				) => {
					console.log('[onSubmit] Submitting values', values);
					const { contacts, ...otherValues } = values;
					const clientRes = await handleUpdateClient({
						values: otherValues,
						contacts,
						setFieldTouched,
						setFieldError,
						setSubmitting,
					});
					if (clientRes.error) return;

					const contactRes = await handleUpdateContacts(contacts);
					if (contactRes.error) return;

					dispatch(
						showToast({
							message: 'Updated client!',
							success: true,
							autohide: true,
						}),
					);
					resetForm();
				},
			}}
		>
			{({ isSubmitting, values, handleSubmit, touched, errors, validateForm }) => {
				const disabled =
					isSubmitting || updateClientState.isLoading || updateContactState.isLoading;

				return (
					<Form
						style={{
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							minHeight: 0,
						}}
					>
						{dialogView && (
							<FolioDrawerHeader title={`Update ${isProspect ? 'Prospect' : 'Client'}`} />
						)}
						<Box
							sx={{
								flex: 1,
								overflowY: 'auto',
							}}
						>
							<FormikErrorScroll {...{ isSubmitting }} />
							<CardContent>
								{isLoading ||
								fieldsQuery.isLoading ||
								fieldsQuery.isUninitialized ? (
									<Skeleton width='100%' height='3em' />
								) : (
									<Stack direction='column' spacing={2}>
										<ContactFields {...{ disabled }} />
										<Typography variant='form_label'>Client details</Typography>
										<ClientStatusFields {...{ disabled }} />
										<ClientNameFields {...{ disabled }} />
										<ClientClassificationFields {...{ disabled }} />

										<AddressFields {...{ disabled }} />
										<ClientCategoryFields {...{ disabled }} />

										<ClientInfoFields {...{ disabled }} />
										<CreationInfoFields {...{ disabled }} />

										<LegalInfoFields {...{ disabled }} />
										<CategoryFields {...{ disabled }} />

										<BankInfoFields {...{ disabled }} />
									</Stack>
								)}
								<DevJSONView value={values} />
								<DevJSONView value={touched} />
								<DevJSONView value={errors} />
							</CardContent>
						</Box>
						<FolioDrawerFormGuard />
						<FolioDrawerFooter>
							<Button
								color='primary'
								onClick={async () => {
									await handleSubmit();
									setTimeout(() => {
										validateForm().then((v) => {
											console.log('Validate form', v);
											if (v && Object.keys(v).length > 0) {
												// there is an error, show a wee toastie
												let message = '';
												const { contacts, ...rest } = v;
												if (contacts) {
													contacts.filter(Boolean).forEach((c, i) => {
														message += `Error with contact ${i + 1}: ${Object.values(c).join('; ')}. `;
													});
												}
												if (rest) {
													message += `${Object.values(rest).join('; ')}.`;
												}

												dispatch(
													showToast({
														message,
														error: true,
													}),
												);
											}
										});
									}, 100);
								}}
								variant='contained'
								disabled={isLoading || isSubmitting}
							>
								{isSubmitting ? (
									<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
								) : (
									'Save changes'
								)}
							</Button>
						</FolioDrawerFooter>
					</Form>
				);
			}}
		</Formik>
	);
};

const UpdateClientForm = ({ dialogView = false, onSubmit }) => {
	return (
		<>
			<ClientSchemaProvider {...{ view: VIEW }}>
				<UpdateClientFormWrapper {...{ dialogView, onSubmit }} />
			</ClientSchemaProvider>
			<AddContactDialog />
		</>
	);
};
export default UpdateClientForm;
